import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  registerTableMenu,
  unregisterTableMenu,
} from "src/charts/tableMenuRegistry";
// Reuses KpiExportMenu's existing menu chrome (.kpi-export__menu/__item) —
// same portal-dropdown-from-the-header-hamburger pattern as
// DownloadOptionsMenu, just with one always-available client-side item
// instead of backend-declared ones (this export reads data already loaded,
// no has_download_options gate needed).
import "./KpiExportMenu.scss";

const MENU_WIDTH = 190;

type Props = {
  // The card-header hamburger (WidgetRenderer → openTableMenu) is the only
  // trigger: this menu registers under the widget id used as container id,
  // same convention as KpiExportMenu/DownloadOptionsMenu.
  menuId?: string;
  onDownload: () => void | Promise<void>;
  isDownloading?: boolean;
};

export default function PracticeDetailsMenu({
  menuId,
  onDownload,
  isDownloading,
}: Props) {
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const openAt = useCallback((pos: { x: number; y: number }) => {
    setMenuPos({
      x: Math.min(
        Math.max(pos.x - MENU_WIDTH, 8),
        window.innerWidth - MENU_WIDTH - 8,
      ),
      y: Math.min(pos.y + 6, window.innerHeight - 120),
    });
  }, []);

  const close = useCallback(() => setMenuPos(null), []);

  useEffect(() => {
    if (!menuId) return;
    registerTableMenu(menuId, openAt);
    return () => unregisterTableMenu(menuId);
  }, [menuId, openAt]);

  useEffect(() => {
    if (!menuPos) return;
    const onPointerDown = (e: PointerEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuPos, close]);

  if (!menuPos) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="kpi-export__menu"
      role="menu"
      style={{ left: menuPos.x, top: menuPos.y, width: MENU_WIDTH }}
    >
      <button
        type="button"
        className="kpi-export__item"
        disabled={isDownloading}
        onClick={async () => {
          await onDownload();
          close();
        }}
      >
        <i
          className={`bi ${isDownloading ? "bi-hourglass-split" : "bi-file-earmark-excel"}`}
          aria-hidden="true"
        />
        Download Details
      </button>
    </div>,
    document.body,
  );
}
